interface GeneratedRequestOptions {
  method?: string;
  headers?: Record<string, string>;
  data?: unknown;
  body?: BodyInit | null;
  params?: Record<string, unknown>;
  signal?: AbortSignal;
}

const baseUrl = import.meta.env.VITE_API_FNB_BASE_URL || 'https://localhost:5208';

function requestUrl(pathname: string, params?: Record<string, unknown>): string {
  const url = new URL(pathname.replace(/^\//, ''), `${baseUrl.replace(/\/$/, '')}/`);
  Object.entries(params ?? {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null) url.searchParams.set(key, String(value));
  });
  return url.href;
}

function cacheMode(method: string, pathname: string): RequestCache {
  const catalogRead =
    method === 'GET' &&
    (/\/ordering-sessions\/menu$/.test(pathname) ||
      /\/ordering-sessions\/products\//.test(pathname) ||
      /\/products(?:\/best-sellers|\/[^/]+)?$/.test(pathname) ||
      /\/stores$/.test(pathname) ||
      /\/menus(?:\/[^/]+\/items)?$/.test(pathname) ||
      /\/moods(?:\/[^/]+\/items)?$/.test(pathname));
  return catalogRead ? 'default' : 'no-store';
}

export default async function publicAnonymousRequest<T>(
  pathname: string,
  options: GeneratedRequestOptions = {}
): Promise<T> {
  const method = (options.method ?? 'GET').toUpperCase();
  const response = await fetch(requestUrl(pathname, options.params), {
    method,
    headers: { 'Content-Type': 'application/json', ...options.headers },
    body: options.data !== undefined ? JSON.stringify(options.data) : options.body,
    signal: options.signal,
    credentials: 'omit',
    cache: cacheMode(method, pathname),
    redirect: 'error',
    referrerPolicy: 'no-referrer',
  });
  if (!response.ok) {
    let data: { message?: string; errors?: Array<{ code?: string }> } = {};
    try {
      data = (await response.json()) as typeof data;
    } catch {
      /* Deliberately discard untrusted response text. */
    }
    const error = Object.assign(
      new Error(data.message || `Public request failed (${response.status})`),
      { data, response: { status: response.status } }
    );
    throw error;
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}
